'use client';
import cn from 'classnames';
import styles from './ReviewsBlock.module.scss';
import { ProductDetailedType, ReviewType } from '@/types';
import { Button } from '@/atoms/Button';
import { Toggle } from '@/components/Toggle';
import { useCallback, useEffect, useState } from 'react';
import { ReviewItem } from '@/components/ReviewItem';
import { fetchData } from '@/services';
import { Loader } from '@/atoms/Loader';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectShowLeaveReviewModal,
  setShowAuthModal,
  setShowLeaveReviewModal,
} from '@/store';
import { Modal } from '@/atoms/Modal';
import { useAdaptiveLayout } from '@/hooks/useAdaptiveLayout';
import { BottomDrawer } from '@/atoms/BottomDrawer';
import { LeaveReviewForm } from '@/modules/Product/LeaveReviewForm';
import { EmptyReviewBlock } from '@/components/EmptyReviewBlock';
import { getTokens } from '@/services';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

type ReviewsBlockProps = {
  className?: string;
  items: ReviewType[];
  product: ProductDetailedType;
};

export function ReviewsBlock({ className, items, product }: ReviewsBlockProps) {
  const wrapperClassName = cn(styles.wrapper, className);
  const [onlyWithPhoto, setOnlyWithPhoto] = useState(false);
  const [reviews, setReviews] = useState<ReviewType[]>(items);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const showLeaveReviewModal = useSelector(selectShowLeaveReviewModal);
  const { isTablet } = useAdaptiveLayout();
  const { accessToken } = getTokens();
  useBodyScrollLock(showLeaveReviewModal);

  const handleToggle = () => {
    setOnlyWithPhoto(!onlyWithPhoto);
  };

  const handleLeaveReview = () => {
    if (!accessToken) {
      dispatch(setShowAuthModal(true));
    } else {
      dispatch(setShowLeaveReviewModal(true));
    }
  };

  const handleCloseLeaveReviewModal = () => {
    dispatch(setShowLeaveReviewModal(false));
  };

  const updateReviews = useCallback(async () => {
    setLoading(true);

    const photoQuery = onlyWithPhoto ? '&withPhoto=Y' : '';
    const reviews = await fetchData({
      url: `/form/review/list?productId=${product.id}${photoQuery}`,
    });

    if (reviews.success) {
      setReviews(reviews.data.list);
    }

    setLoading(false);
  }, [onlyWithPhoto, product.id]);

  useEffect(() => {
    updateReviews();
  }, [updateReviews]);

  return (
    <div className={wrapperClassName}>
      <div className={styles.header}>
        <Toggle
          label="Только с фото"
          checked={onlyWithPhoto}
          onChange={handleToggle}
        />
        <div className={styles.button}>
          <Button
            variant="secondary"
            text="Оставить отзыв"
            onClick={handleLeaveReview}
          />
        </div>
      </div>
      {loading && <Loader />}
      {reviews.length === 0 && !loading && (
        <EmptyReviewBlock text="Пока нет отзывов" />
      )}
      {reviews.length > 0 && !loading && (
        <div className={styles.list}>
          {reviews.map((review) => (
            <ReviewItem
              review={review}
              key={review.id}
              onReact={updateReviews}
            />
          ))}
        </div>
      )}
      <Modal
        showCloseButton={true}
        active={showLeaveReviewModal && !isTablet}
        onClose={handleCloseLeaveReviewModal}
      >
        <LeaveReviewForm product={product} />
      </Modal>
      <BottomDrawer
        active={showLeaveReviewModal && !!isTablet}
        onClose={handleCloseLeaveReviewModal}
      >
        <LeaveReviewForm product={product} />
      </BottomDrawer>
    </div>
  );
}
